// Telemetry for failed requests whose response body is thrown away.
//
// A call made with the `SuppressErrors` header (and any call whose caller swallows the
// rejection) leaves nothing behind but an auto-collected dependency row: a status code and a
// URL, no body. The Graph or backend error code that says *why* the call failed is dropped on
// the floor. HAM-4292 stalled on exactly that — a 403 on the e-mail search that could only be
// explained by asking the customer to reproduce it with devtools open.
//
// What is sent is the collapsed path (the same placeholders the dependency initializers use),
// the status, the error code and message, and the *names* of the query parameters. The
// parameter values stay out: they carry the search term and the dossier reference, which are
// client data.

import { AxiosError } from 'axios'
import { appInsights, isExpectedGraphFailure, toPathOnlyName } from './appInsights'
import { GraphError } from '../context/AxiosContext'
import { normalizeAxiosError } from '../reducers/errorReducer'

export const REQUEST_FAILED_EVENT = 'Request.Failed'

const MAX_MESSAGE_LENGTH = 500

function getCollapsedName(error: AxiosError): string {
    const method = (error.config?.method || 'get').toUpperCase()
    const baseUrl = error.config?.baseURL || ''
    const url = error.config?.url || ''
    // A relative url is appended to the base url the way axios does it; toPathOnlyName keeps
    // only the path either way, so a missing base url costs nothing.
    const separator = baseUrl && url && !baseUrl.endsWith('/') && !url.startsWith('/') ? '/' : ''
    return toPathOnlyName(`${method} ${baseUrl}${separator}${url}`)
}

// Which OData parameters the call used is the whole diagnosis for HAM-4292: a folder listing
// that browses ($filter) succeeds where the same URL with $search does not. The values are
// deliberately dropped.
export function getQueryParameterNames(error: AxiosError): string {
    const params = error.config?.params
    const fromConfig = params && typeof params === 'object' ? Object.keys(params) : []
    const queryIndex = (error.config?.url || '').indexOf('?')
    const fromUrl = queryIndex >= 0 ? Array.from(new URLSearchParams((error.config?.url || '').slice(queryIndex + 1)).keys()) : ([] as string[])
    return Array.from(new Set([...fromConfig, ...fromUrl]))
        .sort()
        .join(',')
}

export function buildRequestFailureProperties(error: AxiosError): Record<string, unknown> {
    const normalized = normalizeAxiosError(error)
    const graphInnerError = (error.response?.data as GraphError)?.error?.innerError
    return {
        name: getCollapsedName(error),
        destination: error.config?.headers?.['Destination'],
        httpStatus: error.response?.status,
        errorCode: normalized.code,
        errorType: normalized.type,
        errorMessage: String(normalized.message ?? '').substring(0, MAX_MESSAGE_LENGTH),
        queryParameters: getQueryParameterNames(error),
        // Suppressed calls are the ones that leave no trace anywhere else; the flag says whether
        // the user saw anything at all.
        suppressed: !!error.config?.headers?.['SuppressErrors'],
        // Microsoft needs this id to look a failed Graph call up on their side.
        graphRequestId: graphInnerError?.['request-id'],
    }
}

/**
 * Sends the error code of a failed request to Application Insights. Skips calls that never got
 * a response (network errors and cancellations, which carry no code to report) and the two
 * Graph calls that fail as part of their normal use.
 */
export function trackRequestFailure(error: AxiosError): void {
    if (!error?.response) {
        return
    }
    if (isExpectedGraphFailure(getCollapsedName(error), error.response.status)) {
        return
    }
    appInsights.trackEvent({ name: REQUEST_FAILED_EVENT, properties: buildRequestFailureProperties(error) })
}
